import {
	autorun,
	computed,
	IAutorunOptions,
	IReactionDisposer,
	IReactionOptions,
	IReactionPublic,
	IWhenOptions,
	Lambda,
	makeAutoObservable,
	makeObservable,
	observable,
	reaction,
	when,
} from 'mobx';
import { debounce } from 'lodash';

import { KeysMatching } from 'tools/types';
import { i18n } from 'core/localization';

export enum ValidationState {
	Valid = 'valid',
	Invalid = 'invalid',
	Validating = 'validating',
}

export interface ValidatableModel<TModel extends object> {
	validator: ModelValidator<TModel>;
}

export interface BindingProps<TValue> {
	value: TValue;
	onChange: (value: TValue) => void;
	invalid?: boolean;
	errors?: string[];
	validationState?: ValidationState;
}

export type LinkModelOptions = {
	validation?: true;
};

export function linkModel<TModel extends object, U extends keyof TModel>(
	model: TModel,
	property: U,
	options?: LinkModelOptions,
) {
	const validatableModel = model as ValidatableModel<TModel>;
	const result = {
		value: model[property],
		onChange: (value: TModel[U]) => (model[property] = value),
	} as BindingProps<TModel[U]>;

	//validation should not be placed directly on a field, it is handled by FormEntry so the following fields should not matter
	//if something is broken please contact me before uncommenting this. Vasiliy

	if (options?.validation && validatableModel.validator) {
		result.validationState = validatableModel.validator.getValidationState(property);
		result.invalid = validatableModel.validator.getValidationState(property) != ValidationState.Valid;
		result.errors = validatableModel.validator.getErrors(property);
	}

	return result;
}

class ValidationRule {
	key: string;
	callback: () => ValidationState | boolean;
	callbackAsync: () => Promise<ValidationState> | Promise<boolean>;
	reactionExpression?: () => unknown;
	message: string;
	validationState: ValidationState = ValidationState.Validating;
	requiredRule: boolean = false;
	private mobx = new MobxManager();
	runCount = -1;

	constructor(init?: Partial<ValidationRule>) {
		Object.assign(this, init);

		makeObservable(this, {
			validationState: observable,
			revalidated: computed,
			runCount: observable,
		});

		this.init();
	}

	//the property shows if validation has been re-run since initialization
	//we need it to not show validation errors on initial form load
	get revalidated() {
		return this.runCount > 0;
	}

	init() {
		if (this.callbackAsync) {
			const bouncedCallback = debounce(async () => {
				const result = await this.callbackAsync();
				this.runCount++;
				this.setValidationResult(result);
			}, 1000);

			this.mobx.reaction(
				this.reactionExpression,
				async () => {
					this.validationState = ValidationState.Validating;
					await bouncedCallback();
				},
				{
					fireImmediately: true,
				},
			);
		} else if (this.reactionExpression) {
			this.mobx.reaction(
				this.reactionExpression,
				() => {
					const result = this.callback();
					this.runCount++;
					this.setValidationResult(result);
				},
				{
					fireImmediately: true,
				},
			);
		} else {
			this.mobx.reaction(
				() => this.callback(),
				(result) => {
					this.runCount++;
					this.setValidationResult(result);
				},
			);
		}
	}

	async validate() {
		const result = this.callbackAsync ? await this.callbackAsync() : this.callback();
		this.setValidationResult(result);
	}

	setValidationResult(result: ValidationState | boolean) {
		if (typeof result == 'boolean') {
			this.validationState = result ? ValidationState.Valid : ValidationState.Invalid;
		} else {
			this.validationState = result;
		}
	}

	destroy() {
		this.mobx.destroy();
	}
}

class FieldValidator {
	requiredRule?: ValidationRule;
	rules: ValidationRule[] = [];
	mobx = new MobxManager();

	get valid() {
		return this.validationState == ValidationState.Valid;
	}

	get revalidated() {
		return this.rules.some((x) => x.revalidated);
	}

	get validationState() {
		let result = ValidationState.Valid;

		for (const rule of this.rules) {
			result = getWorseValidationState(result, rule.validationState);
		}

		return result;
	}

	get requiredValidationState() {
		if (!this.requiredRule) return ValidationState.Valid;

		return this.requiredRule.validationState;
	}

	get errors() {
		return this.rules.filter((x) => x.validationState == ValidationState.Invalid).map((x) => x.message);
	}

	constructor(init?: Partial<FieldValidator>) {
		Object.assign(this, init);

		makeObservable(this, {
			validationState: computed,
			requiredValidationState: computed,
			errors: computed,
			rules: observable,
			valid: computed,
			revalidated: computed,
		});
	}

	addRule(options: AddRuleOptions) {
		const rule = new ValidationRule(options);
		if (options.requiredRule) {
			this.requiredRule = rule;
		}
		this.rules.push(rule);
		return rule;
	}

	validate() {
		this.rules.forEach((x) => {
			x.validate();
			x.runCount++;
		});
	}

	destroy() {
		this.mobx.destroy();
		this.rules.forEach((x) => x.destroy());
	}
}

type AddRuleOptions = Partial<
	Pick<ValidationRule, 'message' | 'reactionExpression' | 'callback' | 'callbackAsync' | 'requiredRule' | 'key'>
> & {};

export class ModelValidator<TModel extends object = object> {
	model: TModel;
	submitted: boolean = false;

	static emailRegex =
		/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

	validations: Record<string, FieldValidator>;

	subValidators: ModelValidator<object>[] = [];

	get validationState(): ValidationState {
		const selfState = Object.entries<FieldValidator>(this.validations).reduce(
			(result, [, validator]) => getWorseValidationState(validator.validationState, result),
			ValidationState.Valid,
		);

		return getWorseValidationState(selfState, this.subValidatorsState);
	}

	get subValidatorsState() {
		let result = ValidationState.Valid;

		for (const subValidator of this.iterateSubValidators()) {
			result = getWorseValidationState(subValidator.validationState, result);
		}

		return result;
	}

	get errors() {
		const result: { field: string; errors: string[] }[] = [];

		Object.entries<FieldValidator>(this.validations).forEach(([field, validator]) => {
			if (validator.validationState == ValidationState.Invalid) {
				return result.push({ field, errors: validator.errors });
			}
		}, []);

		for (const subValidator of this.iterateSubValidators()) {
			if (subValidator.errors.length) {
				result.splice(0, 0, ...subValidator.errors);
			}
		}

		return result;
	}

	get valid(): boolean {
		return this.validationState == ValidationState.Valid;
	}

	constructor(model: TModel) {
		this.validations = {};
		this.model = model;

		makeObservable(this, {
			validations: observable,
			valid: computed,
			validationState: computed,
			subValidatorsState: computed,
			errors: computed,
			submitted: observable,
			subValidators: observable.shallow,
		});
	}

	addSubValidator(subValidator: ModelValidator<object>) {
		this.subValidators.push(subValidator);
	}

	removeSubValidator(subValidator: ModelValidator<object>) {
		const index = this.subValidators.indexOf(subValidator);
		if (index != -1) {
			this.subValidators.splice(index, 1);
		}
	}

	getFieldValidator(field: string, createIfNotExists: boolean = false) {
		if (this.validations[field] == null && createIfNotExists) {
			this.validations[field] = new FieldValidator();
		}

		return this.validations[field];
	}

	add<TField extends keyof TModel>(field: TField, options: AddRuleOptions = {}): ModelValidator<TModel> {
		options.reactionExpression ??= () => this.model[field];

		this.getFieldValidator(field as string, true).addRule(options);

		return this;
	}

	addGeneric(key: string, options: AddRuleOptions = {}): ModelValidator<TModel> {
		this.getFieldValidator(key, true).addRule(options);
		return this;
	}

	update<TField extends keyof TModel>(field: TField, options: AddRuleOptions = {}): ModelValidator<TModel> {
		options.reactionExpression ??= () => this.model[field];

		this.remove(field, options.key);
		const validator = this.getFieldValidator(field as string, true);
		validator.addRule(options);

		return this;
	}

	remove<TField extends keyof TModel>(field: TField, key: string): ModelValidator<TModel> {
		const validator = this.getFieldValidator(field as string, true);
		const rule = validator.rules.splice(
			validator.rules.findIndex((x) => x.key == key),
			1,
		);
		rule?.[0]?.destroy();
		return this;
	}

	*iterateSubValidators() {
		for (const [, fieldValue] of Object.entries(this.model)) {
			if (fieldValue && typeof fieldValue == 'object' && 'validator' in fieldValue) {
				// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
				yield fieldValue.validator as ModelValidator<object>;
			}
		}
		for (const subValidator of this.subValidators) {
			yield subValidator;
		}
	}

	required<TField extends keyof TModel>(
		field: TField,
		applyIf?: () => boolean,
		message?: string,
		allowEmptyArrays: boolean = false,
	): ModelValidator<TModel> {
		if (message == null) {
			message = i18n('Required field');
		}

		this.add(field, {
			message,
			callback: () => {
				if (applyIf != null && !applyIf()) return true;

				const value = this.model[field];
				if (value == null) return false;

				if (typeof value === 'string' && value.trim() == '') {
					return false;
				}

				return !(!allowEmptyArrays && Array.isArray(value) && !value.length);
			},
			reactionExpression: () => [applyIf?.(), this.model[field]],
		});

		this.getFieldValidator(field as string).requiredRule =
			this.validations[field as string].rules[this.validations[field as string].rules.length - 1];

		return this;
	}

	between<TField extends KeysMatching<TModel, number>>(
		field: TField,
		from: number,
		to: number,
		includeBorders: boolean = true,
		message?: string,
	) {
		if (!message) {
			message = i18n('The input value should be between {0} and {1}', from, to);
		}

		this.add(field, {
			message,
			callback: () => {
				const value = this.model[field] as number;
				if (includeBorders) {
					return value >= from && value <= to;
				} else {
					return value > from && value < to;
				}
			},
		});
		return this;
	}

	getValidationState<TField extends keyof TModel>(field: TField) {
		if (this.validations[field as string] == null) return ValidationState.Valid;

		return this.validations[field as string].validationState;
	}

	getErrors<TField extends keyof TModel>(field: TField): string[] {
		if (this.validations[field as string] == null) return [];

		return this.validations[field as string].errors;
	}

	isRequired<TField extends keyof TModel>(field: TField): boolean {
		return this.validations[field as string]?.requiredRule?.validationState != ValidationState.Valid;
	}

	validate = () => {
		this.submitted = true;
		Object.keys(this.validations)
			.map((key: string) => this.validations[key])
			.filter((value) => value != null)
			.forEach((value) => value.validate());

		this.subValidators.forEach((x) => x.validate());
	};

	destroy() {
		Object.entries<FieldValidator>(this.validations).forEach(([, validator]) => validator.destroy());
	}
}

export class MobxManager {
	disposers: IReactionDisposer[] = [];

	constructor(container?: { destroy: () => void }) {
		if (container) {
			const originalDestroy = container.destroy;
			container.destroy = () => {
				this.destroy();
				originalDestroy.apply(container);
			};
		}
	}

	when(predicate: () => boolean, effect: Lambda, opts?: IWhenOptions) {
		const disposer = when(predicate, effect, opts);
		this.saveDisposer(disposer);
	}

	reaction<T>(
		expression: (r: IReactionPublic) => T,
		effect: (arg: T, prev: T, r: IReactionPublic) => void | Promise<void>,
		opts: IReactionOptions<T, boolean> = {},
	) {
		const disposer = reaction(expression, effect, opts);
		return this.saveDisposer(disposer);
	}

	autorun(view: (r: IReactionPublic) => unknown, opts: IAutorunOptions = {}) {
		const disposer = autorun(view, opts);
		return this.saveDisposer(disposer);
	}

	destroy() {
		this.disposers.forEach((x) => x());
	}

	saveDisposer(disposer: IReactionDisposer) {
		this.disposers.push(disposer);

		return (() => {
			disposer();
			const index = this.disposers.indexOf(disposer);
			this.disposers.splice(index, 1);
		}) as IReactionDisposer;
	}
}

const order = [ValidationState.Invalid, ValidationState.Validating, ValidationState.Valid];

export function getWorseValidationState(currentState: ValidationState, resultingState: ValidationState) {
	for (const state of order) {
		if (state == currentState || state == resultingState) return state;
	}

	return ValidationState.Valid;
}

export function addUniqueNameValidation<T extends { name: string; validator: ModelValidator<T> }>(
	model: T,
	isNameUniqueCallback: (name: string) => Promise<boolean>,
) {
	const initialName = model.name;

	model.validator.add('name', {
		callbackAsync: async () => {
			if (!model.name) return true;

			if (initialName && initialName == model.name) return true;

			return await isNameUniqueCallback(model.name);
		},
		message: i18n('Name exists'),
	});
}

export class SingleToArrayConverter<T extends object, V> {
	constructor(
		private target: T,
		private targetField: KeysMatching<T, V[]>,
	) {
		makeAutoObservable(this);
	}

	get value() {
		const fieldValue = this.target[this.targetField] as V[];
		if (fieldValue?.length > 0) {
			return fieldValue[0];
		}

		return null;
	}

	set value(value: V) {
		(this.target[this.targetField] as V[]) = [value];
	}
}

export function addWidgetExistsValidation<T extends object>(
	validator: ModelValidator<T>,
	getWidgetExists: () => boolean,
	message: string,
) {
	validator.addGeneric('widgetExists', {
		reactionExpression: getWidgetExists,
		message,
		callback: () => !getWidgetExists(),
	});
}

export function addNameValidation<T extends object>(
	entity: { name: string },
	validator: ModelValidator<T>,
	checkCallback: (name: string) => Promise<boolean>,
) {
	const initialName = entity.name;

	validator.addGeneric('name', {
		callbackAsync: async () => {
			if (!entity.name) return true;

			if (initialName && initialName?.toUpperCase() == entity.name?.toUpperCase()) return true;

			return await checkCallback(entity.name);
		},
		reactionExpression: () => entity.name,
		message: i18n('Name exists'),
	});
}

export class ValidationManager {
	validators: Map<unknown, ModelValidator> = new Map();

	constructor() {
		makeAutoObservable(this);
	}

	add(validator: ModelValidator) {
		this.validators.set(validator.model, validator);
	}

	get<T extends object>(model: T) {
		return this.allValidators.get(model) as ModelValidator<T>;
	}

	getValidatorsCallbacksList: (() => ModelValidator[])[] = [];

	addValidatorsCallback(callback: () => ModelValidator[]) {
		this.getValidatorsCallbacksList.push(callback);
	}

	get allValidators() {
		const map = new Map<unknown, ModelValidator>(this.validators);
		this.getValidatorsCallbacksList.forEach((callback) => {
			callback().forEach((validator) => map.set(validator.model, validator));
		});
		return map;
	}

	get rootValidator() {
		const result = new ModelValidator({});
		this.allValidators.forEach((x) => result.addSubValidator(x));
		return result;
	}
}
